import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const dropdownRef = useRef(null);
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();

  // 14 vendor categories with proper mapping in the exact order provided
  const vendorCategories = [
    { name: 'Catering Services', slug: 'catering-services' },
    { name: 'Decorators', slug: 'decorators' },
    { name: 'Bands & Banjo', slug: 'bands-banjo' },
    { name: 'Photographers & Content Creators', slug: 'photographers-content-creators' },
    { name: 'Makeup Artists', slug: 'makeup-artists' },
    { name: 'Venues', slug: 'venues' },
    { name: 'DJ & Music', slug: 'dj-music' },
    { name: 'Invitation Cards', slug: 'invitation-cards' },
    { name: 'Function Wear', slug: 'function-wear' },
    { name: 'Stage & Entrance Decorations', slug: 'stage-entrance-decorations' },
    { name: 'Cakes & Gift Shops', slug: 'cakes-gift-shops' },
    { name: 'Anchors & Hosts', slug: 'anchors-hosts' },
    { name: 'Entertainment & Extras', slug: 'entertainment-extras' },
    { name: 'Sweets & Snacks', slug: 'sweets-snacks' }
  ];

  const handleSearch = (e) => {
    e.preventDefault();
    const query = selectedCategory || searchQuery;
    if (query.trim()) {
      navigate(`/vendors?search=${encodeURIComponent(query)}`);
      setShowDropdown(false);
    }
  };

  const handleCategorySelect = (categoryObj) => {
    setSelectedCategory(categoryObj.name);
    setSearchQuery(categoryObj.name);
    setShowDropdown(false);
    // Navigate directly to category page using proper slug
    navigate(`/category/${categoryObj.slug}`);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact-section');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <nav className="bg-blue-600 shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="https://res.cloudinary.com/dgban38oz/image/upload/v1759933174/Logo_apj7y5.png"           // 👈 THIS IS THE LOGO LINK
              alt="Ceremonzy Logo" 
              className="h-10 w-10 object-contain"
            />
            <span className="text-2xl font-bold text-white">
              Ceremonzy
            </span>
          </Link>

          {/* Search Bar - Desktop */}
          <div className="hidden md:flex flex-1 max-w-4xl mx-8">
            <form onSubmit={handleSearch} className="w-full">
              <div className="relative" ref={dropdownRef}>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setShowDropdown(true);
                  }}
                  onFocus={() => setShowDropdown(true)}
                  placeholder="Search ceremony vendors..."
                  className="w-full px-6 py-3 pl-12 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                />
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                  <svg className={`h-5 w-5 text-gray-400 transition-transform ${showDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                
                {/* Dropdown Menu */}
                {showDropdown && (
                  <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    <div className="grid grid-cols-2 gap-1 p-2">
                      {vendorCategories
                        .filter(categoryObj => 
                          searchQuery === '' || 
                          categoryObj.name.toLowerCase().includes(searchQuery.toLowerCase())
                        )
                        .map((categoryObj, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => handleCategorySelect(categoryObj)}
                            className="text-left px-3 py-2 text-sm rounded transition-colors"
                            style={{color: '#800000'}}
                            onMouseEnter={(e) => {e.target.style.backgroundColor = '#FFD700'; e.target.style.color = '#800000';}}
                            onMouseLeave={(e) => {e.target.style.backgroundColor = 'transparent'; e.target.style.color = '#800000';}}
                          >
                            {categoryObj.name}
                          </button>
                        ))
                      }
                    </div>
                  </div>
                )}
              </div>
            </form>
          </div>

          {/* Navigation Links - Desktop */}
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className="px-3 py-2 text-sm font-medium text-white hover:text-gray-200 transition-colors flex items-center space-x-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              <span>Home</span>
            </Link>
            <Link to="/about" className="px-3 py-2 text-sm font-medium text-white hover:text-gray-200 transition-colors">
              About
            </Link>
            
            <Link to="/register-vendor" className="px-3 py-2 text-sm font-medium text-white hover:text-gray-200 transition-colors">
              List Your Business
            </Link>
            
            {/* Contact Info */}
            <button 
              onClick={scrollToContact}
              className="px-3 py-2 text-sm font-medium text-white hover:text-gray-200 transition-colors flex items-center space-x-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span>Contact</span>
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="focus:outline-none transition-colors"
              style={{color: '#FFFFFF'}}
              onMouseEnter={(e) => e.target.style.color = '#FFD700'}
              onMouseLeave={(e) => e.target.style.color = '#FFFFFF'}
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Search Bar */}
        <div className="md:hidden pb-3">
          <form onSubmit={handleSearch}>
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for ceremony vendors..."
                className="w-full px-4 py-2 pl-10 pr-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
          </form>
        </div>

        {/* Mobile Navigation Menu */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-blue-300">
            <div className="py-2 space-y-1">
              <Link
                to="/"
                className="flex items-center space-x-2 px-3 py-2 text-white hover:bg-blue-500 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                <span>Home</span>
              </Link>
              <Link
                to="/about"
                className="block px-3 py-2 text-white hover:bg-blue-500 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                About
              </Link>
              
              <Link
                to="/register-vendor"
                className="block px-3 py-2 text-white hover:bg-blue-500 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                List Your Business
              </Link>
              
              <button
                onClick={() => {
                  scrollToContact();
                  setIsMenuOpen(false);
                }}
                className="flex items-center space-x-2 px-3 py-2 text-white hover:bg-blue-500 transition-colors w-full text-left"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span>Contact</span>
              </button>

            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;